'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Anchor, Box, Button, Container, Group, SimpleGrid, Stack, Text } from '@mantine/core';
import type { Locale } from '../../i18n/locales';
import type { SiteSettingsData } from '../../types/content';
import { getSupabaseBrowserClientSafe } from '../../lib/supabase/client';
import { getCarComparisonLinkProps, resolvePublicHref } from '../../lib/content/comparisonLinks';
import { filterVisibleFooterLinks } from '../../lib/content/footerLinks';

type SocialLinkRow = {
  label?: string;
  href?: string;
};

const toPlainText = (value?: string) =>
  value
    ? value
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    : '';

export function Footer({ locale, settings }: { locale: Locale; settings: SiteSettingsData }) {
  const [footerData, setFooterData] = useState<SiteSettingsData>(() => settings);

  useEffect(() => {
    setFooterData(settings);
  }, [settings]);

  useEffect(() => {
    const client = getSupabaseBrowserClientSafe();
    if (!client) return;

    const loadFooter = async () => {
      const [{ data: settingsRow }, { data: footerRows }, { data: socialRows }] = await Promise.all([
        client.from('site_settings').select('*').eq('locale', locale).order('updated_at', { ascending: false }).limit(1).maybeSingle(),
        client.from('site_footer_links').select('*').eq('locale', locale).order('order', { ascending: true }),
        client.from('site_social_links').select('*').eq('locale', locale).order('order', { ascending: true }),
      ]);

      setFooterData((prev) => ({
        ...prev,
        siteTitle: settingsRow?.title || prev.siteTitle,
        footerBrand: settingsRow?.footer_brand || settingsRow?.title || prev.footerBrand || prev.siteTitle,
        footerCopyright: settingsRow?.footer_copyright || prev.footerCopyright,
        primaryCtaLabel: settingsRow?.primary_cta_label || undefined,
        primaryCtaHref: settingsRow?.primary_cta_href || undefined,
        footerLinks: filterVisibleFooterLinks(
          (footerRows || [])
            .map((row) => ({
              groupLabel: row.group_label || undefined,
              label: row.label || '',
              href: row.href || '',
            }))
            .filter((row) => row.label && row.href),
        ),
        socialLinks: socialRows?.length
          ? (socialRows as SocialLinkRow[])
            .map((row) => ({
              label: row.label || '',
              href: row.href || '',
            }))
            .filter((row) => row.label && row.href)
          : prev.socialLinks,
      }));
    };

    const channel = client
      .channel('footer-live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'site_settings' }, loadFooter)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'site_footer_links' }, loadFooter)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'site_social_links' }, loadFooter)
      .subscribe();

    void loadFooter();

    return () => {
      void client.removeChannel(channel);
    };
  }, [locale]);

  const footerLinks = filterVisibleFooterLinks([...(footerData.footerLinks || [])]);

  // Backward compatibility for old path in existing DB rows.
  footerLinks.forEach((link) => {
    if (link.href === '/tietosuojaseloste') {
      link.href = '/tietosuoja';
      if (!link.label || link.label.toLowerCase().includes('seloste')) {
        link.label = locale === 'fi' ? 'Tietosuoja' : locale === 'sv' ? 'Dataskydd' : 'Privacy';
      }
    }
  });

  const groups: Record<string, typeof footerLinks> = {};
  const groupOrder: string[] = [];
  footerLinks.forEach((link) => {
    const key = link.groupLabel || 'Links';
    if (!groups[key]) {
      groups[key] = [];
      groupOrder.push(key);
    }
    groups[key].push(link);
  });

  const groupEntries = groupOrder.map((key) => [key, groups[key]] as const);
  const footerBrand = footerData.footerBrand || footerData.siteTitle;
  const footerHeadline =
    toPlainText(footerData.body) ||
    (locale === 'fi'
      ? 'Luotettu lähde vakuutuspäätöksiin.'
      : locale === 'sv'
        ? 'Den pålitliga källan för försäkringsbeslut.'
        : 'The trusted source for insurance decisions.');
  const footerCopyright =
    footerData.footerCopyright ||
    (locale === 'fi'
      ? 'Palvelemme vakuutuksissa, vertailussa ja neuvonnassa. Kaikki oikeudet pidätetään.'
      : locale === 'sv'
        ? 'Vi hjälper dig med försäkringar, jämförelser och rådgivning. Alla rättigheter förbehållna.'
        : 'We help with insurance comparisons and guidance. All rights reserved.');
  const footerCtaLabel = footerData.primaryCtaLabel?.trim();
  const footerCtaHref = footerData.primaryCtaHref?.trim();
  const resolvedFooterCtaHref = footerCtaHref
    ? resolvePublicHref(locale, footerCtaHref, { includeLegacyAutovakuutus: true })
    : undefined;
  const socialLinks = footerData.socialLinks || [];
  return (
    <Box component="footer" mt={96} style={{ borderTop: '1px solid var(--surface-border)', background: '#fff' }}>
      <Container size="xl" py={{ base: 56, md: 80 }}>
        <Stack gap={46}>
          <Group justify="space-between" align="end" wrap="wrap" gap="lg">
            <Text
              fw={500}
              style={{
                color: 'var(--text-primary)',
                fontSize: 'clamp(2rem, 3vw, 2.5rem)',
                lineHeight: 1.3,
                maxWidth: 580,
              }}
            >
              {footerHeadline}
            </Text>
            {footerCtaLabel && footerCtaHref ? (
              <Button
                component={Link}
                href={resolvedFooterCtaHref || footerCtaHref}
                {...getCarComparisonLinkProps(resolvedFooterCtaHref)}
                variant="default"
                styles={{
                  root: {
                    background: 'var(--surface-soft)',
                    borderColor: 'var(--surface-border)',
                    color: 'var(--text-primary)',
                  },
                }}
              >
                {footerCtaLabel}
              </Button>
            ) : null}
          </Group>

          <SimpleGrid cols={{ base: 1, sm: 2, md: Math.min(5, Math.max(2, groupEntries.length)) }} spacing={20}>
            {groupEntries.map(([groupLabel, links]) => (
              <Box
                key={groupLabel}
                p={24}
                style={{
                  border: '1px solid var(--surface-border)',
                  borderRadius: 'var(--radius-xl)',
                  background: 'var(--surface-base)',
                }}
              >
                <Stack gap={8}>
                  <Text size="xs" fw={500} tt="uppercase" style={{ color: 'var(--text-muted)', letterSpacing: '0.04em' }}>
                    {groupLabel}
                  </Text>
                  {links.slice(0, 6).map((l) => {
                    const href = resolvePublicHref(locale, l.href);
                    return (
                      <Anchor
                        key={`${l.label}-${l.href}`}
                        component={Link}
                        href={href}
                        {...getCarComparisonLinkProps(href)}
                        underline="never"
                        size="sm"
                        style={{ color: 'var(--text-primary)', fontWeight: 500 }}
                      >
                        {l.label}
                      </Anchor>
                    );
                  })}
                </Stack>
              </Box>
            ))}
          </SimpleGrid>

          <Stack gap={18}>
            <Group justify="space-between" align="start" wrap="wrap" gap="md">
              <Text fw={600} size="xl" style={{ color: 'var(--text-primary)' }}>
                {footerBrand}
              </Text>
              {socialLinks.length ? (
                <Group gap={18} wrap="wrap">
                  {socialLinks.map((link) => (
                    <Anchor
                      key={`${link.label}-${link.href}`}
                      component={Link}
                      href={link.href}
                      underline="never"
                      size="sm"
                      style={{ color: 'var(--text-primary)', borderBottom: '1px solid var(--text-primary)' }}
                    >
                      {link.label}
                    </Anchor>
                  ))}
                </Group>
              ) : null}
            </Group>
            <Text size="sm" style={{ color: 'var(--text-secondary)', maxWidth: 980 }}>
              {footerCopyright}
            </Text>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
